import React, { useState } from 'react';
import { Box, Button, Input, Select, FormControl, FormLabel, FormErrorMessage, FormHelperText, Alert, AlertIcon, Text, SimpleGrid } from '@chakra-ui/react';
import { MyLocation } from '@mui/icons-material';

import { getCurrentPosition, reverseGeocode, lookupPincode } from '../services/GeoServices';

export const INDIAN_STATES = [
    'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh', 'Chhattisgarh',
    'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir',
    'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
    'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

export const emptyAddress = { house: '', street: '', landmark: '', city: '', state: '', pincode: '' };

// Prefer the structured copy; older accounts only have the single address string
export const addressFromUser = (user) => {
    if (user?.addressDetails && Object.values(user.addressDetails).some(Boolean)) {
        return { ...emptyAddress, ...user.addressDetails };
    }
    return { ...emptyAddress, street: user?.address || '' };
};

export const composeAddress = (a) => {
    const line = [a.house, a.street, a.landmark && `Near ${a.landmark}`, a.city].map(s => (s || '').trim()).filter(Boolean).join(', ');
    const tail = [a.state, a.pincode].map(s => (s || '').trim()).filter(Boolean).join(' - ');
    return [line, tail].filter(Boolean).join(', ');
};

export const validateAddress = (a) => {
    const errors = {};
    if (!a.house.trim()) errors.house = 'Add your flat / house number so the courier can find you.';
    if (!a.street.trim()) errors.street = 'Add the street, area or colony.';
    if (!/^[1-9]\d{5}$/.test(a.pincode.trim())) errors.pincode = 'Enter a valid 6-digit PIN code.';
    if (!a.city.trim()) errors.city = 'Enter your city.';
    if (!a.state.trim()) errors.state = 'Select your state.';
    return errors;
};

// Nominatim state names mostly match; map the few that differ
const matchState = (name) => {
    if (!name) return '';
    const clean = name.replace(/^National Capital Territory of /i, '').trim();
    return INDIAN_STATES.find(s => s.toLowerCase() === clean.toLowerCase()) || clean;
};

const AddressForm = ({ value, onChange, errors = {}, onClearError }) => {

    const [locating, setLocating] = useState(false);
    const [notice, setNotice] = useState(null);
    const [pinInfo, setPinInfo] = useState(null);

    const set = (field, fieldValue) => {
        onChange({ ...value, [field]: fieldValue });
        onClearError && onClearError(field);
    };

    const onClickLocation = async () => {
        setLocating(true);
        setNotice(null);
        try {
            const coords = await getCurrentPosition();
            const found = await reverseGeocode(coords.latitude, coords.longitude);
            // Replace the whole address: mixing a saved address with a new location gives a wrong one.
            // Anything the lookup couldn't find is left blank for the customer to fill.
            const next = {
                ...emptyAddress,
                house: found.house || '',
                street: found.street || '',
                city: found.city || '',
                state: matchState(found.state),
                pincode: found.pincode || ''
            };
            onChange(next);
            ['street', 'city', 'state', 'pincode'].forEach(f => next[f] && onClearError && onClearError(f));
            const missing = [
                !next.house && 'flat / house number',
                !next.street && 'street or area',
                !next.pincode && 'PIN code',
                !next.city && 'city'
            ].filter(Boolean);
            setNotice({
                status: 'info',
                text: missing.length
                    ? `We filled what we could from your location. Please add your ${missing.join(', ').replace(/, ([^,]*)$/, ' and $1')}.`
                    : 'We filled your address from your location. Please check it before paying.'
            });
        } catch (error) {
            setNotice({ status: 'warning', text: error.response?.data?.error || error.message });
        }
        setLocating(false);
    };

    const onChangePincode = (e) => {
        const pin = e.target.value.replace(/\D/g, '').slice(0, 6);
        set('pincode', pin);
        setPinInfo(null);
        if (pin.length === 6) {
            lookupPincode(pin)
                .then(result => {
                    onChange({ ...value, pincode: pin, city: result.city || value.city, state: matchState(result.state) || value.state });
                    onClearError && ['pincode', 'city', 'state'].forEach(onClearError);
                    setPinInfo({ ok: true, text: `${result.city}, ${result.state}` });
                })
                .catch(error => setPinInfo({ ok: false, text: error.response?.data?.error || 'Could not check this PIN code.' }));
        }
    };

    return (
        <Box>
            <Button
                width='100%'
                variant='wipe'
                height='44px'
                fontSize='15px'
                leftIcon={<MyLocation fontSize='small' />}
                onClick={onClickLocation}
                isLoading={locating}
                loadingText='Finding your location…'
                mb={3}
            >
                Use my current location
            </Button>

            {notice &&
                <Alert status={notice.status} borderRadius={6} fontSize={14} mb={3} py={2} >
                    <AlertIcon />
                    {notice.text}
                </Alert>
            }

            <FormControl isInvalid={!!errors.house} mb={3} >
                <FormLabel fontSize={14} mb={1} >Flat / House no., Building</FormLabel>
                <Input autoComplete='address-line1' placeholder='e.g. Flat 203, Shanti Apartments' value={value.house} onChange={(e) => set('house', e.target.value)} />
                <FormErrorMessage>{errors.house}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.street} mb={3} >
                <FormLabel fontSize={14} mb={1} >Street, Area, Colony</FormLabel>
                <Input autoComplete='address-line2' placeholder='e.g. MG Road, C-Scheme' value={value.street} onChange={(e) => set('street', e.target.value)} />
                <FormErrorMessage>{errors.street}</FormErrorMessage>
            </FormControl>

            <FormControl mb={3} >
                <FormLabel fontSize={14} mb={1} >Landmark <Text as='span' color='blackAlpha.500' fontWeight={400} >(optional)</Text></FormLabel>
                <Input placeholder='e.g. Near City Mall' value={value.landmark} onChange={(e) => set('landmark', e.target.value)} />
            </FormControl>

            <FormControl isInvalid={!!errors.pincode} mb={3} >
                <FormLabel fontSize={14} mb={1} >PIN code</FormLabel>
                <Input autoComplete='postal-code' inputMode='numeric' placeholder='6-digit PIN code' value={value.pincode} onChange={onChangePincode} />
                {errors.pincode
                    ? <FormErrorMessage>{errors.pincode}</FormErrorMessage>
                    : <FormHelperText fontSize={13} color={pinInfo ? (pinInfo.ok ? 'green.600' : 'orange.500') : 'blackAlpha.600'} >
                        {pinInfo ? (pinInfo.ok ? `✓ ${pinInfo.text}` : pinInfo.text) : 'City and state fill in automatically from the PIN code.'}
                    </FormHelperText>
                }
            </FormControl>

            <SimpleGrid columns={2} spacing={3} >
                <FormControl isInvalid={!!errors.city} >
                    <FormLabel fontSize={14} mb={1} >City</FormLabel>
                    <Input autoComplete='address-level2' placeholder='City' value={value.city} onChange={(e) => set('city', e.target.value)} />
                    <FormErrorMessage>{errors.city}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.state} >
                    <FormLabel fontSize={14} mb={1} >State</FormLabel>
                    <Select placeholder='Select' value={value.state} onChange={(e) => set('state', e.target.value)} >
                        {!INDIAN_STATES.includes(value.state) && value.state && <option value={value.state}>{value.state}</option>}
                        {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </Select>
                    <FormErrorMessage>{errors.state}</FormErrorMessage>
                </FormControl>
            </SimpleGrid>
        </Box>
    )
}

export default AddressForm;
